import type { UserRole } from '@clarix/shared-types';
import { P, type PermissionKey } from './permission-keys';
import { getPermissionsState } from './permission-store';

/**
 * Permission helpers — dual-mode (dynamic permissions first, role fallback).
 *
 * Each helper checks the permission store first. If permissions are
 * loaded and the relevant key exists, the dynamic value wins. Otherwise,
 * the legacy role-based check is used as a fallback.
 */

// ─── Internal helpers ─────────────────────────────────────────────

function hasPerm(key: PermissionKey): boolean | undefined {
  const state = getPermissionsState();
  if (!state.loaded) return undefined;
  return state.permissions[key] === true;
}

function hasAnyPerm(...keys: PermissionKey[]): boolean | undefined {
  const state = getPermissionsState();
  if (!state.loaded) return undefined;
  return keys.some((k) => state.permissions[k] === true);
}

// ─── Role constants (legacy fallback) ────────────────────────────

// Roles that can view financial data (billing rates, costs)
const FINANCIAL_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN'];
const PROJECT_EDIT_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'];
const TEAM_MANAGEMENT_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN'];
const ALL_PROJECTS_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'];
const CLIENT_DETAILS_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'];

// ─── Permission functions ────────────────────────────────────────

export function canViewFinancials(role: UserRole): boolean {
  return hasPerm(P.PROJECTS_VIEW_FINANCIALS) ?? FINANCIAL_ROLES.includes(role);
}

export function canEditProject(role: UserRole): boolean {
  return hasPerm(P.PROJECTS_EDIT) ?? PROJECT_EDIT_ROLES.includes(role);
}

export function canManageTeam(role: UserRole): boolean {
  return (
    (hasPerm(P.PROJECTS_TEAM_ADD) ||
      hasPerm(P.PROJECTS_TEAM_EDIT) ||
      hasPerm(P.PROJECTS_TEAM_REMOVE)) ??
    TEAM_MANAGEMENT_ROLES.includes(role)
  );
}

export function canViewAllProjects(role: UserRole): boolean {
  return hasPerm(P.PROJECTS_VIEW_ALL) ?? ALL_PROJECTS_ROLES.includes(role);
}

export function canViewClientDetails(role: UserRole): boolean {
  return hasPerm(P.PROJECTS_VIEW_ALL) ?? CLIENT_DETAILS_ROLES.includes(role);
}

export function canViewBillingRates(role: UserRole): boolean {
  return hasPerm(P.TIME_VIEW_RATES) ?? FINANCIAL_ROLES.includes(role);
}

export function canViewBudget(role: UserRole): boolean {
  return hasPerm(P.PROJECTS_VIEW_FINANCIALS) ?? FINANCIAL_ROLES.includes(role);
}

export function canAccessAdminNav(role: UserRole): boolean {
  return hasPerm(P.SETTINGS_VIEW) ?? (role === 'SUPER_ADMIN' || role === 'ADMIN');
}

/**
 * Navigation items that should be hidden for non-admin/PM users
 */
export const ADMIN_ONLY_NAV_ITEMS = ['/clients', '/billing', '/reports', '/resources', '/team'];

/**
 * Check if a navigation path should be visible to a role
 */
export function canAccessNavItem(role: UserRole, path: string): boolean {
  if (role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'PROJECT_MANAGER') {
    return true;
  }

  return !ADMIN_ONLY_NAV_ITEMS.includes(path);
}

/**
 * Project detail tabs that should be hidden for non-admin users
 */
export const ADMIN_ONLY_PROJECT_TABS = ['settings'];

// ── Task Deletion ──────────────────────────────────────────────────────

const TASK_DELETE_ANY_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'];

export function canDeleteTask(
  role: UserRole,
  taskCreatedById: string | undefined,
  currentUserId: string,
): boolean {
  const dynDeleteAny = hasPerm(P.HIERARCHY_TASK_DELETE);
  if (dynDeleteAny === true) return true;

  const dynDeleteOwn = hasPerm(P.HIERARCHY_TASK_DELETE_OWN);
  if (dynDeleteAny !== undefined) {
    // Permissions loaded — use dynamic check
    if (dynDeleteOwn && taskCreatedById && taskCreatedById === currentUserId) return true;
    return false;
  }

  // Fallback to legacy role check
  if (TASK_DELETE_ANY_ROLES.includes(role)) return true;
  return !!taskCreatedById && taskCreatedById === currentUserId;
}

/**
 * Check if user can access a project tab.
 * Tab is visible ONLY if the VIEW permission for that area is true.
 * Create/edit/delete control actions inside the tab, not visibility.
 * Falls back to role-based check only when permissions haven't loaded yet.
 */
export function canAccessProjectTab(role: UserRole, tabId: string): boolean {
  switch (tabId) {
    case 'overview':
      return (
        hasPerm(P.PROJECTS_VIEW_OVERVIEW) ??
        (['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'] as UserRole[]).includes(role)
      );
    case 'modules':
      return (
        hasPerm(P.HIERARCHY_MODULE_VIEW) ??
        (['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'TEAM_LEADER'] as UserRole[]).includes(role)
      );
    case 'features':
      return (
        hasPerm(P.HIERARCHY_FEATURE_VIEW) ??
        (['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'TEAM_LEADER'] as UserRole[]).includes(role)
      );
    case 'tasks':
      return hasAnyPerm(P.HIERARCHY_TASK_STATUS, P.HIERARCHY_TASK_DELETE_OWN) ?? true;
    case 'meetings':
      return hasPerm(P.MEETINGS_VIEW) ?? true;
    case 'team':
      return (
        hasPerm(P.PROJECTS_VIEW_TEAM) ??
        (['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'] as UserRole[]).includes(role)
      );
    case 'settings':
      return (
        hasPerm(P.SETTINGS_PROJECT_VIEW) ??
        (['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER'] as UserRole[]).includes(role)
      );
    case 'budget':
      return (
        hasPerm(P.PROJECTS_VIEW_FINANCIALS) ??
        (['SUPER_ADMIN', 'ADMIN'] as UserRole[]).includes(role)
      );
    default:
      return true;
  }
}

/**
 * Get the permission mode for a tab: what actions the user can perform.
 * Returns an object with view/create/edit/delete booleans for granular UI control.
 */
export function getTabPermissions(tabId: string): {
  canView: boolean;
  canViewAll?: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canManage: boolean;
} {
  const none = {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canManage: false,
  };
  const state = getPermissionsState();
  if (!state.loaded)
    return { canView: true, canCreate: true, canEdit: true, canDelete: true, canManage: true };

  const p = state.permissions;

  switch (tabId) {
    case 'modules':
      return {
        canView: p[P.HIERARCHY_MODULE_VIEW] === true,
        canCreate: p[P.HIERARCHY_MODULE_CREATE] === true,
        canEdit: p[P.HIERARCHY_MODULE_EDIT] === true,
        canDelete: p[P.HIERARCHY_MODULE_DELETE] === true,
        canManage:
          p[P.HIERARCHY_MODULE_CREATE] === true ||
          p[P.HIERARCHY_MODULE_EDIT] === true ||
          p[P.HIERARCHY_MODULE_DELETE] === true,
      };
    case 'features':
      return {
        canView: p[P.HIERARCHY_FEATURE_VIEW] === true,
        canCreate: p[P.HIERARCHY_FEATURE_CREATE] === true,
        canEdit: p[P.HIERARCHY_FEATURE_EDIT] === true,
        canDelete: p[P.HIERARCHY_FEATURE_DELETE] === true,
        canManage:
          p[P.HIERARCHY_FEATURE_CREATE] === true ||
          p[P.HIERARCHY_FEATURE_EDIT] === true ||
          p[P.HIERARCHY_FEATURE_DELETE] === true,
      };
    case 'tasks':
      return {
        canView: p[P.HIERARCHY_TASK_STATUS] === true || p[P.HIERARCHY_TASK_DELETE_OWN] === true,
        canCreate: p[P.HIERARCHY_TASK_CREATE] === true,
        canEdit: p[P.HIERARCHY_TASK_STATUS] === true,
        canDelete: p[P.HIERARCHY_TASK_DELETE] === true || p[P.HIERARCHY_TASK_DELETE_OWN] === true,
        canManage:
          p[P.HIERARCHY_TASK_CREATE] === true ||
          p[P.HIERARCHY_TASK_ASSIGN] === true ||
          p[P.HIERARCHY_TASK_DELETE] === true,
      };
    case 'meetings':
      return {
        canView: p[P.MEETINGS_VIEW] === true,
        canViewAll: p[P.MEETINGS_VIEW_ALL] === true,
        canCreate: p[P.MEETINGS_CREATE] === true,
        canEdit: p[P.MEETINGS_EDIT] === true,
        canDelete: p[P.MEETINGS_DELETE] === true,
        canManage:
          p[P.MEETINGS_CREATE] === true ||
          p[P.MEETINGS_EDIT] === true ||
          p[P.MEETINGS_DELETE] === true ||
          p[P.MEETINGS_CANCEL] === true,
      };
    case 'team':
      return {
        canView: p[P.PROJECTS_VIEW_TEAM] === true,
        canCreate: p[P.PROJECTS_TEAM_ADD] === true,
        canEdit: p[P.PROJECTS_TEAM_EDIT] === true,
        canDelete: p[P.PROJECTS_TEAM_REMOVE] === true,
        canManage:
          p[P.PROJECTS_TEAM_ADD] === true ||
          p[P.PROJECTS_TEAM_EDIT] === true ||
          p[P.PROJECTS_TEAM_REMOVE] === true,
      };
    case 'settings':
      return {
        canView: p[P.SETTINGS_PROJECT_VIEW] === true,
        canCreate: p[P.SETTINGS_PROJECT_CONFIG] === true,
        canEdit: p[P.SETTINGS_PROJECT_CONFIG] === true,
        canDelete: false,
        canManage: p[P.SETTINGS_PROJECT_CONFIG] === true,
      };
    case 'budget':
      return {
        canView: p[P.PROJECTS_VIEW_FINANCIALS] === true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canManage: false,
      };
    default:
      return none;
  }
}

/**
 * Check if user has permissions beyond just tasks for a specific project.
 * If they have module/feature/meeting/team/settings/financials permissions → full view (/projects/{id})
 * If only task permissions → simplified view (/my-projects/{id})
 */
export function hasFullProjectAccess(projectId?: string): boolean {
  const state = getPermissionsState();
  if (!state.loaded) return false;

  // Use project-specific permissions if available
  const projectCache = projectId ? state.projectPermissions[projectId] : undefined;
  const perms = projectCache ? projectCache.permissions : state.permissions;

  // Check for tab VIEW permissions beyond tasks.
  // Only view permissions control routing — create/edit/delete are actions within tabs.
  const fullAccessKeys: PermissionKey[] = [
    P.PROJECTS_VIEW_OVERVIEW,
    P.HIERARCHY_MODULE_VIEW,
    P.HIERARCHY_FEATURE_VIEW,
    P.MEETINGS_VIEW,
    P.PROJECTS_VIEW_TEAM,
    P.SETTINGS_PROJECT_VIEW,
  ];

  return fullAccessKeys.some((k) => perms[k] === true);
}

// ── Project-Scoped View ──────────────────────────────────────────────

/**
 * Determine the effective view mode for a user on a specific project.
 * - PROJECTS_VIEW_ALL → 'admin' (can see all projects with full view)
 * - Has full project access (modules/features/meetings/team/settings) → 'admin'
 * - Only task permissions → 'member' (simplified task-only view)
 */
export function getEffectiveProjectView(
  role: UserRole,
  projectManagerId: string | undefined,
  currentUserId: string | undefined,
  projectId?: string,
): 'admin' | 'member' {
  // Dynamic permission check using per-project permissions when available
  const state = getPermissionsState();
  if (state.loaded && projectId) {
    // Use per-project permissions directly — avoids flicker from switchProject timing
    return hasFullProjectAccess(projectId) ? 'admin' : 'member';
  }

  // Fallback: no projectId or permissions not loaded yet
  const dynViewAll = hasPerm(P.PROJECTS_VIEW_ALL);
  if (dynViewAll === true) return 'admin';
  if (dynViewAll === false) {
    return hasFullProjectAccess() ? 'admin' : 'member';
  }

  // Legacy fallback
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') return 'admin';
  if (role === 'TEAM_LEADER' || role === 'MEMBER') return 'member';
  if (role === 'PROJECT_MANAGER') {
    return projectManagerId && currentUserId && projectManagerId === currentUserId
      ? 'admin'
      : 'member';
  }
  return 'member';
}
